import React, { useState, useEffect, useRef } from 'react';
import { addinLogger, type LogEntry, setDebugMode } from '../utils/addinLogger';

const btnStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '2px 8px',
  fontSize: 12,
  border: '1px solid #ccc',
  borderRadius: 4,
  background: '#f5f5f5',
  cursor: 'pointer',
};

const LEVEL_COLOR: Record<string, string> = {
  info: '#333',
  warn: '#b45309',
  error: '#c42b1c',
  debug: '#666',
};

export const DebugLogPanel: React.FC = () => {
  const [entries, setEntries] = useState<LogEntry[]>(() => addinLogger.getEntries());
  const [collapsed, setCollapsed] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = addinLogger.subscribe(() => {
      setEntries(addinLogger.getEntries());
    });
    return unsub;
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries.length]);

  const handleClear = () => addinLogger.clear();
  const handleDisable = () => {
    setDebugMode(false);
    window.location.reload();
  };

  if (entries.length === 0 && !collapsed) {
    return (
      <div style={{ borderTop: '1px solid #e1dfdd', padding: 8, background: '#f5f5f5' }}>
        <span style={{ color: '#666', fontSize: 12 }}>Debug log (no entries yet)</span>
        <button type="button" style={btnStyle} onClick={handleDisable}>
          Disable debug
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        borderTop: '1px solid #e1dfdd',
        background: '#1e1e1e',
        color: '#d4d4d4',
        fontFamily: 'Consolas, monospace',
        fontSize: 11,
        maxHeight: collapsed ? 32 : 180,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 8px',
          background: '#333',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span style={{ color: '#888', fontSize: 12 }}>
          Debug log ({entries.length}) {collapsed ? '— click to expand' : ''}
        </span>
        {!collapsed && (
          <span onClick={(e) => e.stopPropagation()}>
            <button type="button" style={{ ...btnStyle, marginRight: 4 }} onClick={handleClear}>
              Clear
            </button>
            <button type="button" style={btnStyle} onClick={handleDisable}>
              Disable debug
            </button>
          </span>
        )}
      </div>
      {!collapsed && (
        <div style={{ padding: 8, flex: 1, overflow: 'auto' }}>
          {entries.map((e, i) => (
            <div key={i} style={{ marginBottom: 2, wordBreak: 'break-word' }}>
              <span style={{ color: '#6a9955', marginRight: 6 }}>{e.time}</span>
              <span style={{ color: LEVEL_COLOR[e.level] ?? '#888', marginRight: 6 }}>[{e.level}]</span>
              <span>{e.message}</span>
              {e.detail != null && e.detail !== '' && (
                <div style={{ marginLeft: 14, color: '#888' }}>{String(e.detail)}</div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
};
